import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ClipboardEvent,
  type KeyboardEvent,
} from 'react';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  CheckSquare,
  Copy,
  GripVertical,
  MoreHorizontal,
  Pencil,
  Plus,
  Trash2,
} from 'lucide-react';
import {
  capitalizeFirstWord,
  formatCurrency,
  parsePastedItems,
  type CreatePlanningItemInput,
  type ExpenseItemWithRelations,
  type PlanningItem,
  type SettingListOption,
} from '@peles/shared';
import { DropdownMenu } from '../common/DropdownMenu';
import { SearchableListSelect } from '../common/SearchableListSelect';
import { AttachmentButton } from '../attachments/AttachmentButton';
import { useDialog } from '../../context/DialogContext';
import { useViewMode } from '../../context/ViewModeContext';
import {
  useAddEventItems,
  useDeletePlanningItem,
  useReorderEventItems,
  useUpdatePlanningItem,
} from '../../hooks/usePlanning';
import { unitLabel } from '../../hooks/useItemUnits';
import { extractApiErrorMessage } from '../../utils/apiError';
import { ItemsReconciliationNote } from './ItemsReconciliationNote';
import { RecordConversations } from '../messaging/RecordConversations';
import { useThreadCounts, type ThreadCount } from '../../hooks/useThreadCounts';
import { groupItems, itemLabel, lineName, type ItemsGroupBy } from './itemsGridModel';

/** Attachments on items use this polymorphic entity type. */
export const PLANNING_ITEM_ATTACHMENT_TYPE = 'PLANNING_ITEM';

/** Rows shown at once; a long list has "Show all". */
export const ITEMS_PAGE_SIZE = 100;

export interface ItemsGridProps {
  eventId: string;
  /** The items to show, in the order they are kept. */
  items: PlanningItem[];
  /**
   * Every item of the event, filters aside — what a need's items come to is
   * read from these, so a filter or the page limit never shortens it.
   * Defaults to `items`.
   */
  allItems?: PlanningItem[];
  /** The event's lines (needs) an item can belong to. */
  lines: ExpenseItemWithRelations[];
  units: readonly SettingListOption[];
  groupBy: ItemsGroupBy;
  /**
   * Scoped to one need: new rows go to it, and the Need column is hidden.
   * Without it, new rows go to the need chosen on the add row.
   */
  lineId?: string;
  /**
   * The event's items in their stored order, every one — dragging is offered
   * only when given (an order is written whole, so a filtered view, which
   * holds part of the list, cannot reorder it).
   */
  reorderIds?: string[];
  /** Open an item in the full editor (room, catalog, tasks, attachments). */
  onOpen: (item: PlanningItem) => void;
  /** Phones add through the editor. */
  onAdd: () => void;
}

const CELL_INPUT =
  'w-full bg-transparent px-2 py-1.5 text-sm rounded border border-transparent hover:border-gray-200 focus:border-primary-400 focus:bg-white focus:outline-none focus:ring-1 focus:ring-primary-400';

/** The grid's cells answer to arrow-free keys: Enter goes down a row, Escape puts back. */
function focusCell(row: number, col: string) {
  const el = document.querySelector<HTMLInputElement>(
    `[data-items-grid-row="${row}"][data-items-grid-col="${col}"]`,
  );
  el?.focus();
  el?.select?.();
}

interface CellProps {
  value: string;
  row: number;
  col: string;
  label: string;
  numeric?: boolean;
  align?: 'left' | 'right';
  onCommit: (value: string) => void;
  /** Enter on the last row moves to the add row. */
  onEnterPastEnd: () => void;
  rowCount: number;
}

/**
 * One editable cell: what is typed stays until it is left or Enter is pressed,
 * then is saved if it changed. Escape puts the stored value back.
 */
function GridCell({
  value,
  row,
  col,
  label,
  numeric,
  align,
  onCommit,
  onEnterPastEnd,
  rowCount,
}: CellProps) {
  const [draft, setDraft] = useState(value);
  const editing = useRef(false);
  useEffect(() => {
    if (!editing.current) setDraft(value);
  }, [value]);

  // Once per edit: Enter commits and then moves focus, and Escape blurs after
  // putting the value back, so the blur that follows must not commit again —
  // it would see the typed draft, since the state update has not rendered yet.
  function commit() {
    if (!editing.current) return;
    editing.current = false;
    if (draft !== value) onCommit(draft);
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      e.preventDefault();
      commit();
      if (row + 1 < rowCount) focusCell(row + 1, col);
      else onEnterPastEnd();
    } else if (e.key === 'Escape') {
      editing.current = false;
      setDraft(value);
      (e.target as HTMLInputElement).blur();
    }
  }

  return (
    <input
      type={numeric ? 'number' : 'text'}
      inputMode={numeric ? 'decimal' : undefined}
      min={numeric ? 0 : undefined}
      step={numeric ? 'any' : undefined}
      value={draft}
      onFocus={() => (editing.current = true)}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={onKeyDown}
      aria-label={label}
      data-items-grid-row={row}
      data-items-grid-col={col}
      className={`${CELL_INPUT} ${align === 'right' ? 'text-right' : ''}`}
    />
  );
}

/** A non-negative amount from a cell, rounded to the cent; null when it is not one. */
function readAmount(text: string): number | null {
  if (text.trim() === '') return 0;
  const n = Number(text);
  return Number.isFinite(n) && n >= 0 ? Math.round(n * 100) / 100 : null;
}

interface RowProps {
  item: PlanningItem;
  row: number;
  rowCount: number;
  showLine: boolean;
  lines: ExpenseItemWithRelations[];
  units: readonly SettingListOption[];
  draggable: boolean;
  onPatch: (
    item: PlanningItem,
    patch: Parameters<ReturnType<typeof useUpdatePlanningItem>['mutate']>[0]['updates'],
  ) => void;
  onOpen: (item: PlanningItem) => void;
  onDuplicate: (item: PlanningItem) => void;
  onDelete: (item: PlanningItem) => void;
  onEnterPastEnd: () => void;
  /** This item's conversation count, fetched once for the grid (H3). */
  threadCount: ThreadCount | null;
  onThreadActivity: () => void;
}

/** An item as a conversation record — `THREAD_ENTITY_TYPES`' spelling. */
const ITEM_THREAD_ENTITY = 'PlanningItem';

function ItemRow({
  item,
  row,
  rowCount,
  showLine,
  lines,
  units,
  draggable,
  onPatch,
  onOpen,
  onDuplicate,
  onDelete,
  onEnterPastEnd,
  threadCount,
  onThreadActivity,
}: RowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.id,
    disabled: !draggable,
  });
  const name = itemLabel(item);
  return (
    <tr
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
      }}
      className="border-t border-gray-100 align-middle"
      data-testid="items-grid-row"
    >
      <td className="w-8 pl-1">
        {draggable && (
          <button
            type="button"
            {...attributes}
            {...listeners}
            aria-label={`Move ${name}`}
            className="p-1 text-gray-300 hover:text-gray-500 cursor-grab active:cursor-grabbing touch-none"
          >
            <GripVertical size={16} />
          </button>
        )}
      </td>
      <td className="min-w-[12rem]">
        <GridCell
          value={item.itemName ?? item.catalogItemName ?? ''}
          row={row}
          col="name"
          label={`Item ${row + 1}`}
          rowCount={rowCount}
          onEnterPastEnd={onEnterPastEnd}
          onCommit={(text) => {
            const trimmed = capitalizeFirstWord(text.trim());
            if (!trimmed) return;
            // A name typed over a catalog item makes it free text.
            onPatch(item, { itemName: trimmed, catalogItemId: null });
          }}
        />
      </td>
      <td className="w-20">
        <GridCell
          value={String(item.quantity)}
          row={row}
          col="qty"
          label={`Quantity of ${name}`}
          numeric
          align="right"
          rowCount={rowCount}
          onEnterPastEnd={onEnterPastEnd}
          onCommit={(text) => {
            const quantity = readAmount(text);
            if (quantity !== null) onPatch(item, { quantity });
          }}
        />
      </td>
      <td className="w-32">
        <SearchableListSelect
          items={[...units]}
          value={item.unit}
          onChange={(v) => onPatch(item, { unit: typeof v === 'string' && v ? v : null })}
          getValue={(u) => u.value}
          getLabel={(u) => u.label}
          placeholder={item.unit ? unitLabel(units, item.unit) : '—'}
          clearable
          ariaLabel={`Unit of ${name}`}
        />
      </td>
      <td className="w-28">
        <GridCell
          value={String(item.unitPrice)}
          row={row}
          col="price"
          label={`Unit price of ${name}`}
          numeric
          align="right"
          rowCount={rowCount}
          onEnterPastEnd={onEnterPastEnd}
          onCommit={(text) => {
            const unitPrice = readAmount(text);
            if (unitPrice !== null) onPatch(item, { unitPrice });
          }}
        />
      </td>
      <td className="w-28 px-2 text-right text-sm font-medium text-gray-900 tabular-nums">
        {formatCurrency(item.total)}
      </td>
      <td className="w-32 px-2 text-sm text-gray-600 truncate" title={item.roomName ?? undefined}>
        {item.roomName ?? <span className="text-gray-300">—</span>}
      </td>
      {showLine && (
        <td className="min-w-[10rem]">
          {item.expenseItemId ? (
            <SearchableListSelect
              items={lines}
              value={item.expenseItemId}
              onChange={(v) => typeof v === 'string' && v && onPatch(item, { expenseItemId: v })}
              getValue={(l) => l.id}
              getLabel={(l) => lineName(l)}
              ariaLabel={`Need of ${name}`}
            />
          ) : (
            <SearchableListSelect
              items={lines}
              value={null}
              onChange={(v) => typeof v === 'string' && v && onPatch(item, { expenseItemId: v })}
              getValue={(l) => l.id}
              getLabel={(l) => lineName(l)}
              placeholder="Needs a line — pick one"
              ariaLabel={`Need of ${name}`}
            />
          )}
        </td>
      )}
      <td className="w-44 pr-1">
        <div className="flex items-center justify-end gap-0.5">
          {item.openTaskCount > 0 && (
            <button
              type="button"
              onClick={() => onOpen(item)}
              className="inline-flex items-center gap-0.5 px-1.5 py-1 text-xs text-gray-600 rounded hover:bg-gray-100"
              title={`${item.openTaskCount} open task${item.openTaskCount === 1 ? '' : 's'}`}
              aria-label={`${item.openTaskCount} open tasks for ${name}`}
            >
              <CheckSquare size={14} /> {item.openTaskCount}
            </button>
          )}
          <RecordConversations
            entityType={ITEM_THREAD_ENTITY}
            entityId={item.id}
            variant="icon"
            count={threadCount}
            onActivity={onThreadActivity}
          />
          <AttachmentButton entityType={PLANNING_ITEM_ATTACHMENT_TYPE} entityId={item.id} />
          <DropdownMenu
            align="end"
            ariaLabel={`More for ${name}`}
            items={[
              {
                id: 'open',
                label: 'Details, tasks, room…',
                icon: <Pencil size={14} />,
                onSelect: () => onOpen(item),
              },
              {
                id: 'dup',
                label: 'Duplicate',
                icon: <Copy size={14} />,
                onSelect: () => onDuplicate(item),
              },
              {
                id: 'delete',
                label: 'Delete',
                icon: <Trash2 size={14} />,
                dividerBefore: true,
                onSelect: () => onDelete(item),
              },
            ]}
            trigger={(props) => (
              <button
                type="button"
                {...props}
                className="p-1.5 text-gray-400 hover:text-gray-700 rounded hover:bg-gray-100"
              >
                <MoreHorizontal size={16} />
              </button>
            )}
          />
        </div>
      </td>
    </tr>
  );
}

/**
 * The event's items as a grid you type into. Each cell saves as you leave it
 * (Enter goes down a row, the last row's Enter goes to the add row, Escape
 * puts a cell back); the add row takes a name and Enter, or a paste of rows
 * from a spreadsheet (Item · Qty · Unit price · Notes), all onto one need.
 * Rows duplicate, drag to reorder (in the list's own order), and group by
 * need, vendor, room or category with subtotals — by need, each group says
 * how its items stand against the budget or the agreed price. On a phone it
 * is a list of cards, each opening the editor.
 */
export function ItemsGrid({
  eventId,
  items,
  allItems = items,
  lines,
  units,
  groupBy,
  lineId,
  reorderIds,
  onOpen,
  onAdd,
}: ItemsGridProps) {
  const { isMobile } = useViewMode();
  const { confirm } = useDialog();
  const update = useUpdatePlanningItem();
  const addItems = useAddEventItems();
  const remove = useDeletePlanningItem();
  const reorder = useReorderEventItems();
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [showAll, setShowAll] = useState(false);
  const [newName, setNewName] = useState('');
  const [newQty, setNewQty] = useState('1');
  const [newPrice, setNewPrice] = useState('');
  const [newLine, setNewLine] = useState<string | null>(lineId ?? null);
  const [order, setOrder] = useState<string[] | null>(null);
  const addInput = useRef<HTMLInputElement>(null);
  const targetLine = lineId ?? newLine;

  // Drag reorders what is shown; it is offered only for the whole list, ungrouped.
  const canReorder = !!reorderIds && groupBy === 'none' && !isMobile;
  useEffect(() => setOrder(null), [items]);
  const ordered = useMemo(() => {
    if (!order) return items;
    const byId = new Map(items.map((i) => [i.id, i]));
    return order.map((id) => byId.get(id)).filter((i): i is PlanningItem => !!i);
  }, [items, order]);

  const visible = showAll ? ordered : ordered.slice(0, ITEMS_PAGE_SIZE);
  const hidden = ordered.length - visible.length;
  const groups = useMemo(
    () => groupItems(visible, groupBy, lines, allItems),
    [visible, groupBy, lines, allItems],
  );
  const showLine = !lineId;
  // Every visible row's conversation badge in one request (COMMUNICATIONS-DESIGN
  // H3). The grid is an event's — never a template's — so every row is a record.
  const { countFor: threadCountFor, refetch: refetchThreadCounts } = useThreadCounts(
    ITEM_THREAD_ENTITY,
    visible.map((item) => item.id),
  );
  const onThreadActivity = useCallback(() => {
    void refetchThreadCounts();
  }, [refetchThreadCounts]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  function fail(err: unknown, fallback: string) {
    setError(extractApiErrorMessage(err, fallback));
  }

  function patch(item: PlanningItem, updates: Parameters<typeof update.mutate>[0]['updates']) {
    setError('');
    update.mutate(
      { itemId: item.id, updates },
      { onError: (err) => fail(err, 'The item could not be saved') },
    );
  }

  async function addRows(rows: CreatePlanningItemInput[], what: string) {
    setError('');
    setNotice('');
    if (!targetLine) {
      setError('Choose the need these items are for first.');
      return false;
    }
    try {
      const line = lines.find((l) => l.id === targetLine);
      const created = await addItems.mutateAsync({
        eventId,
        items: rows.map((r) => ({
          ...r,
          expenseItemId: targetLine,
          vendorCategoryId: r.vendorCategoryId ?? line?.vendorCategoryId ?? null,
        })),
      });
      if (created.length > 1) setNotice(`Added ${created.length} ${what}.`);
      return true;
    } catch (err) {
      fail(err, 'The items could not be added');
      return false;
    }
  }

  async function addTyped() {
    const name = capitalizeFirstWord(newName.trim());
    if (!name) return;
    const quantity = readAmount(newQty) || 1;
    const unitPrice = readAmount(newPrice) ?? 0;
    if (await addRows([{ itemName: name, quantity, unitPrice }], 'items')) {
      setNewName('');
      setNewQty('1');
      setNewPrice('');
      addInput.current?.focus();
    }
  }

  async function onPaste(e: ClipboardEvent<HTMLInputElement>) {
    const text = e.clipboardData.getData('text/plain');
    // One line is an ordinary paste into the name; several are rows.
    if (!text.includes('\n') && !text.includes('\t')) return;
    e.preventDefault();
    const rows = parsePastedItems(text);
    if (!rows.length) return;
    await addRows(
      rows.map((r) => ({
        itemName: r.itemName,
        quantity: r.quantity,
        unitPrice: r.unitPrice,
        notes: r.notes ?? undefined,
      })),
      'pasted items',
    );
  }

  function duplicate(item: PlanningItem) {
    setError('');
    addItems.mutate(
      {
        eventId,
        items: [
          {
            vendorCategoryId: item.vendorCategoryId,
            catalogItemId: item.catalogItemId,
            itemName: item.itemName ?? undefined,
            quantity: item.quantity,
            roomId: item.roomId,
            expenseItemId: item.expenseItemId,
            unitPrice: item.unitPrice,
            unit: item.unit,
            notes: item.notes ?? undefined,
            // Right after the original: the same place, the copy after it.
            sortOrder: item.sortOrder,
          },
        ],
      },
      { onError: (err) => fail(err, 'The item could not be duplicated') },
    );
  }

  async function del(item: PlanningItem) {
    const tasks = item.openTaskCount
      ? ` Its ${item.openTaskCount} open task${item.openTaskCount === 1 ? '' : 's'} stay on the Tasks list.`
      : '';
    if (
      !(await confirm({
        title: 'Delete item',
        message: `Delete “${itemLabel(item)}”?${tasks}`,
        confirmLabel: 'Delete',
      }))
    )
      return;
    remove.mutate(item.id, { onError: (err) => fail(err, 'The item could not be deleted') });
  }

  function onDragEnd({ active, over }: DragEndEvent) {
    if (!over || active.id === over.id || !reorderIds) return;
    const from = reorderIds.indexOf(String(active.id));
    const to = reorderIds.indexOf(String(over.id));
    if (from < 0 || to < 0) return;
    const next = arrayMove(reorderIds, from, to);
    setOrder(next);
    reorder.mutate(
      { eventId, itemIds: next },
      {
        onError: (err) => {
          setOrder(null);
          fail(err, 'The new order could not be saved');
        },
      },
    );
  }

  const messages = (
    <>
      {error && (
        <p role="alert" className="mb-2 text-sm text-red-600">
          {error}
        </p>
      )}
      {notice && <p className="mb-2 text-sm text-green-700">{notice}</p>}
    </>
  );

  if (isMobile) {
    return (
      <div>
        {messages}
        {groups.map((group) => (
          <section key={group.key || 'none'} className="mb-4" aria-label={group.label || 'Items'}>
            {group.label && (
              <div className="flex items-baseline justify-between mb-1">
                <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                  {group.label}
                </h4>
                <span className="text-xs text-gray-500">{formatCurrency(group.subtotal)}</span>
              </div>
            )}
            {group.line && group.reconciliation && (
              <div className="mb-1">
                <ItemsReconciliationNote
                  lineId={group.line.id}
                  reconciliation={group.reconciliation}
                />
              </div>
            )}
            <ul className="space-y-2">
              {group.items.map((item) => (
                <li key={item.id} className="flex items-center bg-white rounded-lg shadow">
                  <button
                    type="button"
                    onClick={() => onOpen(item)}
                    className="flex-1 min-w-0 text-left px-4 py-3 min-h-11"
                    data-testid="items-grid-card"
                  >
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="font-medium text-gray-900">{itemLabel(item)}</span>
                      <span className="font-medium tabular-nums">{formatCurrency(item.total)}</span>
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {item.quantity}
                      {item.unit ? ` ${unitLabel(units, item.unit)}` : ''} ×{' '}
                      {formatCurrency(item.unitPrice)}
                      {item.roomName ? ` · ${item.roomName}` : ''}
                      {showLine && item.expenseItemName ? ` · ${item.expenseItemName}` : ''}
                      {item.openTaskCount > 0 ? ` · ${item.openTaskCount} open tasks` : ''}
                    </div>
                  </button>
                  <span className="pr-1 shrink-0">
                    <RecordConversations
                      entityType={ITEM_THREAD_ENTITY}
                      entityId={item.id}
                      variant="icon"
                      count={threadCountFor(item.id)}
                      onActivity={onThreadActivity}
                    />
                  </span>
                </li>
              ))}
            </ul>
          </section>
        ))}
        {hidden > 0 && (
          <button
            type="button"
            onClick={() => setShowAll(true)}
            className="min-h-11 text-sm text-primary-600"
          >
            Show all {ordered.length}
          </button>
        )}
        <button
          type="button"
          onClick={onAdd}
          className="mt-2 w-full min-h-11 inline-flex items-center justify-center gap-1 border border-dashed border-gray-300 rounded-lg text-sm text-gray-700"
        >
          <Plus size={16} /> Add an item
        </button>
      </div>
    );
  }

  let rowIndex = -1;
  const colCount = showLine ? 9 : 8;
  return (
    <div>
      {messages}
      <div className="overflow-x-auto bg-white rounded-lg shadow">
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <table className="w-full text-sm" data-testid="items-grid">
            <thead className="bg-gray-50 text-xs font-medium text-gray-500 uppercase tracking-wide">
              <tr>
                <th className="w-8" aria-label="Order" />
                <th className="text-left px-2 py-2">Item</th>
                <th className="text-right px-2 py-2">Qty</th>
                <th className="text-left px-2 py-2">Unit</th>
                <th className="text-right px-2 py-2">Unit price</th>
                <th className="text-right px-2 py-2">Total</th>
                <th className="text-left px-2 py-2">Room</th>
                {showLine && <th className="text-left px-2 py-2">Need</th>}
                <th aria-label="Actions" />
              </tr>
            </thead>
            <SortableContext
              items={visible.map((i) => i.id)}
              strategy={verticalListSortingStrategy}
            >
              {groups.map((group) => (
                <tbody key={group.key || 'none'}>
                  {group.label && (
                    <tr className="bg-gray-50/60 border-t border-gray-200">
                      <td colSpan={colCount} className="px-3 py-2">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <span className="text-xs font-semibold uppercase tracking-wide text-gray-600">
                            {group.label} · {group.items.length}
                          </span>
                          <span className="flex flex-wrap items-center gap-3">
                            {group.line && group.reconciliation ? (
                              <ItemsReconciliationNote
                                lineId={group.line.id}
                                reconciliation={group.reconciliation}
                              />
                            ) : (
                              <span className="text-xs font-medium text-gray-700">
                                {formatCurrency(group.subtotal)}
                              </span>
                            )}
                          </span>
                        </div>
                      </td>
                    </tr>
                  )}
                  {group.items.map((item) => {
                    rowIndex += 1;
                    return (
                      <ItemRow
                        key={item.id}
                        item={item}
                        row={rowIndex}
                        rowCount={visible.length}
                        showLine={showLine}
                        lines={lines}
                        units={units}
                        draggable={canReorder}
                        onPatch={patch}
                        onOpen={onOpen}
                        onDuplicate={duplicate}
                        onDelete={(i) => void del(i)}
                        onEnterPastEnd={() => addInput.current?.focus()}
                        threadCount={threadCountFor(item.id)}
                        onThreadActivity={onThreadActivity}
                      />
                    );
                  })}
                </tbody>
              ))}
            </SortableContext>
            <tbody>
              <tr className="border-t border-gray-200 bg-gray-50/40">
                <td className="pl-2 text-gray-400">
                  <Plus size={14} />
                </td>
                <td>
                  <input
                    ref={addInput}
                    type="text"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    onPaste={(e) => void onPaste(e)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        void addTyped();
                      }
                    }}
                    placeholder="Add an item — or paste rows from a spreadsheet"
                    aria-label="New item"
                    className={CELL_INPUT}
                  />
                </td>
                <td>
                  <input
                    type="number"
                    inputMode="decimal"
                    min={0}
                    value={newQty}
                    onChange={(e) => setNewQty(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && void addTyped()}
                    aria-label="New item quantity"
                    className={`${CELL_INPUT} text-right`}
                  />
                </td>
                <td />
                <td>
                  <input
                    type="number"
                    inputMode="decimal"
                    min={0}
                    value={newPrice}
                    onChange={(e) => setNewPrice(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && void addTyped()}
                    placeholder="0.00"
                    aria-label="New item unit price"
                    className={`${CELL_INPUT} text-right`}
                  />
                </td>
                <td />
                <td />
                {showLine && (
                  <td>
                    <SearchableListSelect
                      items={lines}
                      value={newLine}
                      onChange={(v) => setNewLine(typeof v === 'string' && v ? v : null)}
                      getValue={(l) => l.id}
                      getLabel={(l) => lineName(l)}
                      placeholder="Which need?"
                      ariaLabel="New item need"
                    />
                  </td>
                )}
                <td className="pr-2 text-right">
                  <button
                    type="button"
                    onClick={() => void addTyped()}
                    disabled={!newName.trim() || addItems.isPending}
                    className="px-3 py-1.5 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50"
                  >
                    Add
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </DndContext>
      </div>
      {hidden > 0 && (
        <button
          type="button"
          onClick={() => setShowAll(true)}
          className="mt-2 text-sm text-primary-600"
        >
          Show all {ordered.length} items
        </button>
      )}
    </div>
  );
}
